import React, { useState, useEffect } from 'react'
import {
  BookOpen, ChevronRight, CheckCircle, Lightbulb,
  Target, Brain, Star, AlertTriangle,
  FileText, PenTool, Youtube,
  ChevronDown, X,
  MessageCircle, Sparkles
} from 'lucide-react'
import { useProgressStore, useLearnProgress, useUnitProgress } from '../store/progressStore'
import { useVideoStore } from '../store/videoStore'
import ConceptVideoModal from '../components/ConceptVideoModal'
import {
  computePredictedFragments, FRAGMENT_TAG_CONFIG,
  type PredictedFragment
} from '../store/predictedFragments'

interface StoryLine {
  speaker: 'student' | 'teacher' | 'friend' | 'narrator'
  emoji: string
  name: string
  text: string
}
interface StepStory {
  scene: string
  lines: StoryLine[]
  reveal: string
  revealEmoji: string
}

const STEP_STORIES: Record<string, StepStory> = {
  AC01: {
    scene: '🍕 피자 가게. 두 종류의 피자 가격을 알아내는 중.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '지수', text: '페퍼로니 1판과 치즈 2판을 샀더니 23,000원이에요.' },
      { speaker: 'friend', emoji: '🤔', name: '민호', text: '그리고 페퍼로니 2판과 치즈 1판은 22,000원이래.' },
      { speaker: 'teacher', emoji: '👨‍🏫', name: '선생님', text: '이 두 조건을 동시에 만족시키는 가격을 찾는 게 연립방정식이에요!' },
      { speaker: 'student', emoji: '💡', name: '지수', text: '페퍼로니를 x, 치즈를 y라 하면 x+2y=23000, 2x+y=22000이네요.' },
      { speaker: 'teacher', emoji: '👨‍🏫', name: '선생님', text: '맞아요! 두 방정식을 동시에 만족하는 x, y를 구하는 게 연립방정식의 해예요.' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '연립방정식: 두 개 이상의 방정식을 동시에 만족하는 미지수의 값을 구하는 것' },
    ],
    reveal: '연립방정식의 해: 두 방정식을 동시에 참이 되게 하는 (x, y) 쌍 | 보통 방정식 수 = 미지수 수',
    revealEmoji: '🍕',
  },
  AC02: {
    scene: '⚖️ 과학 실험실. 가감법으로 무게를 측정하는 날.',
    lines: [
      { speaker: 'teacher', emoji: '👩‍🔬', name: '연구원', text: 'A+B=7g, A−B=3g. A와 B의 무게를 가감법으로 구해볼게요.' },
      { speaker: 'student', emoji: '🙋', name: '지수', text: '두 식을 더하면 2A=10, A=5g! 그러면 B=2g이에요.' },
      { speaker: 'friend', emoji: '😮', name: '민호', text: '와, 한 미지수를 없애버리니까 쉽게 풀리네요!' },
      { speaker: 'teacher', emoji: '👩‍🔬', name: '연구원', text: '이게 가감법이에요! 두 식을 더하거나 빼서 한 미지수를 소거하는 방법이죠.' },
      { speaker: 'student', emoji: '😄', name: '지수', text: '계수가 같지 않으면 적당히 배수를 곱해서 같게 만들면 되겠군요!' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '가감법: 두 방정식을 더하거나 빼서 한 미지수를 소거 → 일차방정식 → 풀기' },
    ],
    reveal: '가감법 순서: ①소거할 미지수의 계수를 맞춤(배수 곱) ②더하거나 빼기 ③남은 미지수 풀기 ④대입',
    revealEmoji: '⚖️',
  },
  AC03: {
    scene: '🎭 연극 준비. 배우 수와 의상 비용을 계산하는 연출가.',
    lines: [
      { speaker: 'teacher', emoji: '🎬', name: '연출가', text: '남자 배우 x명, 여자 배우 y명. 총 12명이고 의상비가 남자 20만, 여자 15만씩 총 210만원이에요.' },
      { speaker: 'student', emoji: '🙋', name: '지수', text: 'x + y = 12, 20x + 15y = 210. 대입법으로 풀어볼게요.' },
      { speaker: 'friend', emoji: '🤔', name: '민호', text: '첫 번째 식에서 y = 12 − x를 두 번째에 대입하면 되겠다!' },
      { speaker: 'student', emoji: '😄', name: '지수', text: '20x + 15(12−x) = 210 → 5x = 30 → x = 6, y = 6. 각 6명이에요!' },
      { speaker: 'teacher', emoji: '🎬', name: '연출가', text: '대입법은 한 식에서 미지수를 정리해 다른 식에 넣는 방법이에요. 계수가 1인 경우 특히 유용하죠!' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '대입법: 한 식을 y=~~ 꼴로 정리 → 다른 식에 대입 → 풀기 → 역대입' },
    ],
    reveal: '대입법 순서: ①한 미지수를 정리(y=ax+b) ②다른 방정식에 대입 ③한 미지수 풀기 ④역대입',
    revealEmoji: '🎭',
  },
  AC04: {
    scene: '🚂 기차역. 속력과 시간으로 거리를 계산하는 승객.',
    lines: [
      { speaker: 'teacher', emoji: '🚉', name: '역무원', text: '두 도시 A, B 사이는 300km. 기차가 A→B로, 자동차가 B→A로 동시 출발해서 2시간 후 만났어요.' },
      { speaker: 'student', emoji: '🙋', name: '지수', text: '기차 속력 x, 자동차 속력 y라 하면 2x + 2y = 300이에요.' },
      { speaker: 'friend', emoji: '🤔', name: '민호', text: '그런데 방정식이 하나뿐이야. 조건이 하나 더 있나요?' },
      { speaker: 'teacher', emoji: '🚉', name: '역무원', text: '기차가 자동차보다 50km/h 빠르다는 조건: x = y + 50. 이제 연립방정식!' },
      { speaker: 'student', emoji: '😄', name: '지수', text: '2(y+50) + 2y = 300 → 4y = 200 → y=50, x=100. 기차 100, 자동차 50km/h!' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '활용 핵심: 미지수 2개 → 조건 2개 필요 | 거리=속력×시간, 단위 통일 중요' },
    ],
    reveal: '연립방정식 활용: 미지수 개수 = 조건 개수 | 거리·속력·시간, 가격·개수 문제 등에 적용',
    revealEmoji: '🚂',
  },
  AC05: {
    scene: '🌊 수영장. 물 채우기와 빼기 속도 문제.',
    lines: [
      { speaker: 'teacher', emoji: '🏊', name: '관리자', text: 'A관으로 채우면 4시간, B관으로 빼면 6시간. 동시에 하면 얼마나 걸릴까요?' },
      { speaker: 'student', emoji: '🙋', name: '지수', text: '1시간에 A는 1/4, B는 1/6을 처리해요. 채우기니까 1/4 − 1/6 = 1/12. 12시간이에요!' },
      { speaker: 'friend', emoji: '🤔', name: '민호', text: '이건 연립방정식 없이도 되네요. 하지만 두 파이프 모두 채우면?' },
      { speaker: 'teacher', emoji: '🏊', name: '관리자', text: 'A, B 둘 다 채운다면 1/4 + 1/3 = 7/12. 12/7시간 ≈ 1.7시간! 연립방정식으로 복잡한 설정도 가능해요.' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '일의 양 문제: 전체=1, 단위시간 일량=1/T | 연립방정식으로 복잡한 조건 처리' },
    ],
    reveal: '일의 양: 전체 작업=1, T시간에 끝나는 일의 단위시간 처리량=1/T | 연립으로 복수 조건 처리',
    revealEmoji: '🌊',
  },
}

const STEPS = [
  {
    id: 'AC01',
    title: '연립방정식의 뜻과 해',
    subtitle: '두 조건을 동시에 만족시키기',
    icon: '🔗',
    difficulty: '기초',
    difficultyColor: 'bg-green-100 text-green-700',
    color: 'bg-gradient-to-br from-emerald-500 to-teal-600',
    lightColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    textColor: 'text-emerald-600',
    tags: ['연립방정식', '해', '미지수'],
    objective: '연립일차방정식의 뜻을 이해하고, 주어진 순서쌍이 해인지 확인할 수 있다.',
    concept: `연립일차방정식

▶ 정의
  두 개 이상의 일차방정식을 묶어서
  그것들을 동시에 만족하는 미지수의 값을 구하는 것

▶ 표기법
  { x + y = 5
  { 2x − y = 1
  (중괄호로 묶어서 표현)

▶ 해(solution)
  두 방정식을 동시에 참이 되게 하는 (x, y) 값의 쌍
  예) x=2, y=3 → x+y=5 ✓, 2x−y=1 ✓ → 해

▶ 확인 방법
  주어진 (x, y)를 각 방정식에 대입해서
  모두 참이면 해, 하나라도 거짓이면 해가 아님`,
    keyPoints: [
      '연립방정식의 해: 두 방정식 모두 동시에 만족',
      '해 확인: 각 식에 대입해서 모두 성립해야 함',
      '미지수가 2개이므로 방정식도 2개 필요',
      '해는 보통 유일한 순서쌍 (x, y)로 나타남',
    ],
    misconceptions: ['SIM_CHECK', 'SIM_ONE'],
    examples: [
      {
        question: '(x, y) = (2, 3)이 연립방정식 { x+y=5, 2x−y=1 의 해인지 확인하시오.',
        answer: 'x+y: 2+3=5 ✓ | 2x−y: 4−3=1 ✓ → 해이다.',
        explanation: '두 식 모두 대입해서 성립하면 해. 하나라도 틀리면 해 아님.',
      },
      {
        question: '(x, y) = (1, 4)가 { x+y=5, 2x−y=1 의 해인지 확인하시오.',
        answer: 'x+y: 1+4=5 ✓ | 2x−y: 2−4=−2 ≠ 1 ✗ → 해가 아니다.',
        explanation: '두 번째 방정식을 만족하지 않으므로 해가 아님.',
      },
      {
        question: 'x=3, y=k가 x+2y=7의 해일 때 k의 값은?',
        answer: '3+2k=7 → 2k=4 → k=2',
        explanation: 'x값을 대입해서 y값(k)을 찾는다.',
      },
    ],
    practice: {
      question: '다음 중 연립방정식 { 2x+y=7, x−y=2 의 해를 고르시오.\n① (1, 5)  ② (3, 1)  ③ (2, 3)',
      hint: '각 순서쌍을 두 방정식 모두에 대입해 확인하세요.',
      steps: [
        '① (1,5): 2+5=7 ✓, 1−5=−4≠2 ✗ → 해 아님',
        '② (3,1): 6+1=7 ✓, 3−1=2 ✓ → 해!',
        '③ (2,3): 4+3=7 ✓, 2−3=−1≠2 ✗ → 해 아님',
      ],
      answer: '② (3, 1)',
    },
  },
  {
    id: 'AC02',
    title: '가감법',
    subtitle: '더하거나 빼서 미지수 소거하기',
    icon: '➕',
    difficulty: '기초',
    difficultyColor: 'bg-green-100 text-green-700',
    color: 'bg-gradient-to-br from-blue-500 to-indigo-600',
    lightColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    textColor: 'text-blue-600',
    tags: ['가감법', '소거', '계수 맞추기'],
    objective: '가감법을 이용하여 연립일차방정식을 풀 수 있다.',
    concept: `가감법 (elimination method)

▶ 원리
  두 방정식을 더하거나 빼서 한 미지수를 소거

▶ 풀이 단계
  ① 소거할 미지수 결정
  ② 그 미지수의 계수 절댓값을 같게 맞춤
     (필요하면 각 방정식에 적절한 수를 곱함)
  ③ 두 방정식을 더하거나 빼서 미지수 소거
  ④ 남은 방정식을 풀어 한 미지수의 값 구하기
  ⑤ 원래 방정식 중 하나에 대입해 나머지 미지수 구하기

예) { 2x + 3y = 12  ... ①
    { 2x −  y =  4  ... ②
  ①−②: 4y = 8 → y = 2
  ②에 대입: 2x − 2 = 4 → x = 3
  해: x=3, y=2`,
    keyPoints: [
      '같은 계수끼리 더하면 소거(부호 반대), 빼면 소거(같은 부호)',
      '계수가 다르면 LCM(최소공배수)으로 배수 곱하기',
      '한 미지수 구한 뒤 반드시 역대입으로 나머지 구하기',
      '구한 해를 원래 두 방정식에 대입해 검산',
    ],
    misconceptions: ['SIM_ELIM', 'SIM_BACK'],
    examples: [
      {
        question: '{ x + y = 7, x − y = 3 을 가감법으로 풀어라.',
        answer: '두 식 더하기: 2x=10 → x=5 | 역대입: 5+y=7 → y=2 | 해: (5, 2)',
        explanation: '계수가 이미 같으므로 바로 더하면 y 소거. 간단한 경우.',
      },
      {
        question: '{ 2x + 3y = 16, 3x − y = 5 를 가감법으로 풀어라.',
        answer: '②×3: 9x−3y=15 | ①+③: 11x=31... (대신) ②×3+①: 9x−3y+2x+3y=15+16 → 11x=31... 잠깐!\n다시: y소거 위해 ②×3: 9x−3y=15, ①: 2x+3y=16 | 더하면 11x=31→x=31/11...\n더 쉽게: ①+②×3: 2x+3y+9x−3y=16+15 → 11x=31\n또는 x소거: ①×3−②×2: 6x+9y−6x+2y=48−10 → 11y=38→y=38/11\n단순 예: { x+2y=8, 3x−2y=4 → 더하면 4x=12→x=3, y=2.5',
        explanation: '계수가 다를 때는 적절히 배수를 곱해 같게 만든 뒤 소거.',
      },
      {
        question: '{ 3x + 2y = 13, x + 2y = 7 을 가감법으로 풀어라.',
        answer: '①−②: 2x=6 → x=3 | 역대입: 3+2y=7 → y=2 | 해: (3, 2)',
        explanation: 'y의 계수가 이미 같으므로 빼면 y 소거.',
      },
    ],
    practice: {
      question: '가감법으로 연립방정식 { 2x + y = 8, x − y = 1 을 풀어라.',
      hint: '두 방정식을 더하면 y가 소거돼요. 그 후 역대입!',
      steps: [
        '①+②: (2x+x) + (y−y) = 8+1 → 3x = 9 → x = 3',
        '②에 x=3 대입: 3 − y = 1 → y = 2',
        '검산: 2(3)+2=8 ✓, 3−2=1 ✓',
      ],
      answer: 'x = 3, y = 2',
    },
  },
  {
    id: 'AC03',
    title: '대입법',
    subtitle: '한 식을 다른 식에 넣어 풀기',
    icon: '🔁',
    difficulty: '기초',
    difficultyColor: 'bg-green-100 text-green-700',
    color: 'bg-gradient-to-br from-violet-500 to-purple-600',
    lightColor: 'bg-violet-50',
    borderColor: 'border-violet-200',
    textColor: 'text-violet-600',
    tags: ['대입법', '정리', '역대입'],
    objective: '대입법을 이용하여 연립일차방정식을 풀 수 있다.',
    concept: `대입법 (substitution method)

▶ 원리
  한 방정식에서 미지수 하나를 다른 미지수로
  표현한 뒤, 나머지 방정식에 대입

▶ 풀이 단계
  ① 한 방정식을 y = ax + b 꼴로 정리
  ② 다른 방정식의 y 자리에 (ax + b) 대입
  ③ x에 대한 일차방정식 풀기
  ④ 구한 x를 ①의 식에 대입해 y 구하기

예) { y = 2x − 1  ... ①
    { 3x + y = 9  ... ②
  ②에 ① 대입: 3x + (2x−1) = 9
  5x = 10 → x = 2
  ①에 대입: y = 4−1 = 3
  해: x=2, y=3`,
    keyPoints: [
      '계수가 1인 미지수를 먼저 정리하면 편함',
      '대입 후 괄호를 반드시 분배법칙으로 풀기',
      '한 미지수 구한 뒤 원래 정리한 식에 역대입',
      '가감법보다 대입법이 편한 경우: 계수가 1인 식이 있을 때',
    ],
    misconceptions: ['SIM_DIST', 'SIM_BACK'],
    examples: [
      {
        question: '{ y = x + 1, 2x + y = 10 을 대입법으로 풀어라.',
        answer: '2x+(x+1)=10 → 3x=9 → x=3 | y=3+1=4 | 해: (3, 4)',
        explanation: 'y=x+1이 이미 정리된 꼴. 바로 두 번째 식에 대입.',
      },
      {
        question: '{ x + y = 6, 2x − y = 3 을 대입법으로 풀어라.',
        answer: '①에서 y=6−x | ②에 대입: 2x−(6−x)=3 → 3x=9 → x=3 | y=3 | 해: (3, 3)',
        explanation: '①에서 y를 x로 표현. 대입 시 괄호 주의 (부호 반전).',
      },
      {
        question: '{ 3x + 2y = 11, x = y − 1 을 대입법으로 풀어라.',
        answer: '②를 ①에 대입: 3(y−1)+2y=11 → 5y=14 → y=14/5\n더 간단히: x=y−1을 직접 대입\n3(y−1)+2y=11 → 3y−3+2y=11 → 5y=14 → y=2.8, x=1.8',
        explanation: 'x=y−1 꼴이므로 x를 대입. 분수 결과도 가능.',
      },
    ],
    practice: {
      question: '대입법으로 { x − 2y = 0, x + y = 9 를 풀어라.',
      hint: '첫 번째 식에서 x = 2y로 정리한 뒤 두 번째 식에 대입하세요.',
      steps: [
        '①에서 x = 2y',
        '②에 대입: 2y + y = 9 → 3y = 9 → y = 3',
        'x = 2×3 = 6',
        '검산: 6−6=0 ✓, 6+3=9 ✓',
      ],
      answer: 'x = 6, y = 3',
    },
  },
  {
    id: 'AC04',
    title: '연립방정식의 활용',
    subtitle: '미지수 2개 → 조건 2개 설정하기',
    icon: '🎯',
    difficulty: '보통',
    difficultyColor: 'bg-yellow-100 text-yellow-700',
    color: 'bg-gradient-to-br from-amber-500 to-orange-600',
    lightColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    textColor: 'text-amber-600',
    tags: ['활용', '미지수 설정', '조건 2개'],
    objective: '실생활 문제를 연립방정식으로 모델링하여 풀고 결과를 해석할 수 있다.',
    concept: `연립방정식 활용 문제 풀이 절차

① 미지수 설정
   구하는 것을 x, y로 놓기
   (개수, 가격, 속력, 양 등)

② 연립방정식 세우기
   문제의 조건 2개를 각각 방정식으로 표현

③ 연립방정식 풀기
   가감법 또는 대입법 사용

④ 검토
   구한 해가 문제 맥락에 맞는지 확인
   (음수 불가, 자연수 조건, 단위 확인 등)

자주 나오는 유형:
• 가격·개수: 총금액과 총개수 조건
• 거리·속력·시간: 거리=속력×시간
• 농도·혼합: 소금양=농도×전체량
• 나이: 현재 나이와 n년 후 나이`,
    keyPoints: [
      '미지수 2개이면 독립적인 조건 2개 필요',
      '단위를 통일할 것 (km, m; 분, 시간 등)',
      '자연수·정수 조건이 있으면 검토 단계에서 확인',
      '같은 의미의 조건을 두 번 쓰지 않도록 주의',
    ],
    misconceptions: ['SIM_UNIT', 'SIM_CONTEXT'],
    examples: [
      {
        question: '어른 3명과 어린이 2명의 입장료가 29,000원이고, 어른 2명과 어린이 4명의 입장료가 28,000원일 때, 어른과 어린이 각각의 입장료는?',
        answer: '어른 x, 어린이 y | 3x+2y=29000, 2x+4y=28000\n②÷2: x+2y=14000 | ①−③: 2x=15000 → x=7500, y=3250',
        explanation: '총액 조건 2개로 연립방정식 구성. 가감법으로 풀기.',
      },
      {
        question: '두 자리 수에서 각 자리 숫자의 합이 11이고, 십의 자리와 일의 자리를 바꾸면 원래보다 27이 작다. 원래 수는?',
        answer: '십의자리 x, 일의자리 y | x+y=11, (10y+x)=(10x+y)−27\n→ −9x+9y=−27 → y=x−3\n→ x+(x−3)=11 → x=7, y=4 → 원래 수: 74',
        explanation: '두 자리 수=10×십의자리+일의자리. 바꾼 수와의 관계.',
      },
      {
        question: '갑과 을이 1200m를 같은 방향으로 출발. 갑은 분속 80m, 을은 분속 50m. 갑이 먼저 도착한 뒤 되돌아오면 몇 분 후에 두 사람이 만나나?',
        answer: '만나는 시간 t분 후(을 기준)\n갑 위치: 1200−80(t−1200/80)=1200−80t+1200=... (복잡)\n간단히: 만날 때까지 갑의 총거리 + 을의 거리 표현으로 풀기',
        explanation: '거리 문제는 각자 이동거리를 시간×속력으로 표현.',
      },
    ],
    practice: {
      question: '연필 5자루와 지우개 3개의 값은 2300원이고, 연필 3자루와 지우개 5개의 값은 2100원이다. 연필 1자루와 지우개 1개의 가격은 각각 얼마인가?',
      hint: '연필을 x원, 지우개를 y원으로 설정하고 조건 2개로 연립방정식을 세우세요.',
      steps: [
        '연필 x원, 지우개 y원 설정',
        '①: 5x + 3y = 2300',
        '②: 3x + 5y = 2100',
        '①×5 − ②×3: 25x+15y−9x−15y = 11500−6300 → 16x = 5200 → x = 325',
        '①에 대입: 1625 + 3y = 2300 → y = 225',
      ],
      answer: '연필 325원, 지우개 225원',
    },
  },
  {
    id: 'AC05',
    title: '해의 특수한 경우',
    subtitle: '해가 없거나 무수히 많은 경우',
    icon: '∞',
    difficulty: '심화',
    difficultyColor: 'bg-red-100 text-red-700',
    color: 'bg-gradient-to-br from-rose-500 to-pink-600',
    lightColor: 'bg-rose-50',
    borderColor: 'border-rose-200',
    textColor: 'text-rose-600',
    tags: ['해 없음', '무수히 많은 해', '평행', '일치'],
    objective: '연립방정식의 해가 없거나 무수히 많은 경우를 판별하고 기하학적으로 이해할 수 있다.',
    concept: `연립방정식의 해의 종류

▶ 일반적인 경우: 해가 유일
  두 직선이 한 점에서 교차
  → 교점이 유일한 해

▶ 해가 없는 경우 (불능)
  두 직선이 평행 (기울기 같고, y절편 다름)
  예) { x + y = 3
      { x + y = 5
  → 0 = 2 (모순) → 해 없음

▶ 해가 무수히 많은 경우 (부정)
  두 직선이 일치 (같은 직선)
  예) { x + y = 3
      { 2x + 2y = 6
  → 0 = 0 (항등식) → 무수히 많은 해

▶ 판별 조건 (a/a' = b/b' 비교)
  a/a' ≠ b/b' → 유일한 해
  a/a' = b/b' ≠ c/c' → 해 없음
  a/a' = b/b' = c/c' → 무수히 많은 해`,
    keyPoints: [
      '해가 없음(불능): 두 직선 평행, 풀면 모순(0=양수)',
      '해가 무수히 많음(부정): 두 직선 일치, 풀면 항등(0=0)',
      '기울기 비교로 빠르게 판별 가능',
      '계수의 비율로 세 경우 구분',
    ],
    misconceptions: ['SIM_INCON', 'SIM_IDENT'],
    examples: [
      {
        question: '{ 2x − y = 3, 4x − 2y = 8 의 해를 구하시오.',
        answer: '②÷2: 2x−y=4 | ①과 비교: 2x−y=3 ≠ 4 → 모순 → 해 없음(불능)',
        explanation: '두 직선이 평행. 같은 기울기지만 y절편이 달라 교점 없음.',
      },
      {
        question: '{ x + 2y = 4, 2x + 4y = 8 의 해를 구하시오.',
        answer: '②÷2: x+2y=4 → ①과 동일 → 무수히 많은 해(부정)',
        explanation: '두 방정식이 같은 직선. 해는 직선 위의 모든 점.',
      },
      {
        question: '{ x + ay = 3, 2x + 6y = b 가 해가 없으려면 a, b의 조건은?',
        answer: '해가 없으려면 1/2 = a/6 ≠ 3/b\n→ a = 3이고 b ≠ 6',
        explanation: '계수비가 같지만(1/2=3/6) 상수비가 다른 경우 해 없음.',
      },
    ],
    practice: {
      question: '다음 연립방정식의 해의 개수를 판별하시오.\n① { 3x + y = 5, 6x + 2y = 10\n② { x − 2y = 1, 2x − 4y = 5',
      hint: '①: 두 번째 식을 2로 나눠보세요. ②: 두 번째 식을 2로 나눠 비교하세요.',
      steps: [
        '① ÷2: 3x+y=5 → 첫 번째 식과 동일 → 무수히 많은 해(부정)',
        '② ÷2: x−2y=2.5 | 첫 번째: x−2y=1 → 1≠2.5 → 해 없음(불능)',
      ],
      answer: '① 무수히 많은 해  ② 해 없음',
    },
  },
]

const TOTAL_LEARN_STEPS = STEPS.length
const UNIT_ID = 'A2-3'

function PracticeBox({ practice, stepId }: { practice: typeof STEPS[0]['practice'], stepId: string }) {
  const [showHint, setShowHint] = useState(false)
  const [showAnswer, setShowAnswer] = useState(false)
  const [showSteps, setShowSteps] = useState(false)
  return (
    <div className="mt-4 bg-indigo-50 border border-indigo-200 rounded-2xl p-4">
      <p className="text-xs font-bold text-indigo-600 mb-2 flex items-center gap-1">
        <PenTool size={12} /> 연습 문제
      </p>
      <p className="text-sm text-slate-800 font-medium mb-3 whitespace-pre-line">{practice.question}</p>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setShowHint(!showHint)}
          className="text-xs px-3 py-1.5 rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200 font-semibold transition-colors">
          💡 힌트
        </button>
        <button onClick={() => setShowSteps(!showSteps)}
          className="text-xs px-3 py-1.5 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 font-semibold transition-colors">
          📋 풀이 단계
        </button>
        <button onClick={() => setShowAnswer(!showAnswer)}
          className="text-xs px-3 py-1.5 rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-200 font-semibold transition-colors">
          ✅ 정답 확인
        </button>
      </div>
      {showHint && (
        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
          💡 {practice.hint}
        </div>
      )}
      {showSteps && (
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-xl">
          <p className="text-xs font-bold text-blue-700 mb-2">단계별 풀이:</p>
          {practice.steps.map((s, i) => (
            <div key={i} className="flex gap-2 text-xs text-blue-800 mb-1">
              <span className="font-bold text-blue-500 w-4">{i + 1}.</span>
              <span className="font-mono">{s}</span>
            </div>
          ))}
        </div>
      )}
      {showAnswer && (
        <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-800 font-mono whitespace-pre-line">
          {practice.answer}
        </div>
      )}
    </div>
  )
}

function ExampleCard({ example, idx }: { example: typeof STEPS[0]['examples'][0], idx: number }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left">
        <span className="text-sm font-semibold text-slate-700">예제 {idx + 1}: {example.question.slice(0, 28)}...</span>
        <ChevronDown size={14} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 py-3 space-y-2">
          <p className="text-sm text-slate-800 whitespace-pre-line">{example.question}</p>
          <div className="bg-emerald-50 rounded-lg p-3">
            <p className="text-xs font-bold text-emerald-600 mb-1">풀이</p>
            <p className="text-sm font-mono text-emerald-800 whitespace-pre-line">{example.answer}</p>
          </div>
          <p className="text-xs text-slate-500 italic whitespace-pre-line">{example.explanation}</p>
        </div>
      )}
    </div>
  )
}

function StoryCard({ story }: { story: StepStory }) {
  const [lineIdx, setLineIdx] = useState(0)
  const [revealed, setRevealed] = useState(false)
  const speakerColor: Record<string, string> = {
    student: 'bg-blue-100 text-blue-800',
    teacher: 'bg-purple-100 text-purple-800',
    friend: 'bg-green-100 text-green-800',
    narrator: 'bg-amber-100 text-amber-800',
  }
  return (
    <div className="bg-gradient-to-b from-slate-50 to-white border border-slate-200 rounded-2xl p-4">
      <p className="text-xs text-slate-500 italic mb-3">{story.scene}</p>
      <div className="min-h-[80px] mb-3">
        {story.lines.slice(0, lineIdx + 1).map((line, i) => (
          <div key={i} className={`flex gap-2 mb-2 ${i < lineIdx ? 'opacity-50' : ''}`}>
            <span className="text-lg flex-shrink-0">{line.emoji}</span>
            <div>
              {line.name && (
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded mr-1 ${speakerColor[line.speaker]}`}>
                  {line.name}
                </span>
              )}
              <span className="text-sm text-slate-700">{line.text}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mb-3">
        <div className="flex-1 h-1 bg-slate-200 rounded-full">
          <div className="h-1 bg-indigo-400 rounded-full transition-all"
            style={{ width: `${((lineIdx + 1) / story.lines.length) * 100}%` }} />
        </div>
        <span className="text-xs text-slate-400">{lineIdx + 1}/{story.lines.length}</span>
      </div>
      <div className="flex gap-2">
        {lineIdx < story.lines.length - 1 ? (
          <button onClick={() => setLineIdx(i => i + 1)}
            className="flex-1 py-2 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white text-xs font-bold transition-colors flex items-center justify-center gap-1">
            <ChevronRight size={13} /> 다음
          </button>
        ) : (
          <button onClick={() => setRevealed(true)}
            className="flex-1 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold transition-colors flex items-center justify-center gap-1">
            <Sparkles size={13} /> 핵심 개념 보기
          </button>
        )}
        <button onClick={() => { setLineIdx(0); setRevealed(false) }}
          className="px-3 py-2 rounded-xl border border-slate-200 text-xs text-slate-500 hover:bg-slate-50 transition-colors">
          처음
        </button>
      </div>
      {revealed && (
        <div className="mt-3 p-3 bg-indigo-50 border border-indigo-200 rounded-xl flex items-start gap-2">
          <span className="text-xl">{story.revealEmoji}</span>
          <p className="text-xs text-indigo-800 font-medium leading-relaxed">{story.reveal}</p>
        </div>
      )}
    </div>
  )
}

function PredictedFragmentPanel({ fragments }: { fragments: PredictedFragment[] }) {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set())
  const visible = fragments.filter(f => !dismissed.has(f.fragment.id))
  if (visible.length === 0) return null
  return (
    <div className="card border border-amber-200 bg-amber-50">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={14} className="text-amber-500" />
        <p className="text-sm font-bold text-amber-800">AI 예상 파편</p>
        <span className="ml-auto text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full font-bold">
          {visible.length}개
        </span>
      </div>
      <div className="space-y-2">
        {visible.map(f => {
          const cfg = FRAGMENT_TAG_CONFIG[f.tag] ?? FRAGMENT_TAG_CONFIG['review']
          return (
            <div key={f.fragment.id} className={`flex items-start gap-2 p-2 rounded-xl border ${cfg.color}`}>
              <span className="text-base">{f.fragment.emoji}</span>
              <div className="flex-1 min-w-0">
                <p className={`text-xs font-bold ${cfg.color}`}>{f.fragment.title}</p>
                <p className="text-xs text-slate-500 mt-0.5 truncate">{f.fragment.misconceptionLabel}</p>
              </div>
              <button onClick={() => setDismissed(s => new Set([...s, f.fragment.id]))}
                className="text-slate-300 hover:text-slate-500 flex-shrink-0">
                <X size={12} />
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

interface A2_3LearnPageProps { defaultStepId?: string | null }

export default function A2_3LearnPage({ defaultStepId }: A2_3LearnPageProps) {
  const [selectedStep, setSelectedStep] = useState<typeof STEPS[0] | null>(
    defaultStepId ? STEPS.find(s => s.id === defaultStepId) ?? null : null
  )
  const [activeTab, setActiveTab] = useState<'story' | 'concept' | 'examples' | 'practice'>('story')
  const [videoModal, setVideoModal] = useState<{ stepId: string; label: string } | null>(null)

  const { completeStep } = useProgressStore(s => ({ completeStep: s.completeStep }))
  const unitProgress = useUnitProgress(UNIT_ID)
  const learnProgress = useLearnProgress(UNIT_ID)
  const predictedFragments = computePredictedFragments(unitProgress, 3)
  const getApprovedByTag = useVideoStore(s => s.getApprovedByTag)

  const completedCount = learnProgress.completedCount
  const progressPct = learnProgress.pct

  useEffect(() => {
    if (defaultStepId) {
      const step = STEPS.find(s => s.id === defaultStepId)
      if (step) { setSelectedStep(step); setActiveTab('concept') }
    }
  }, [defaultStepId])

  const handleComplete = (step: typeof STEPS[0]) => {
    completeStep(UNIT_ID, step.id, step.title)
    setSelectedStep(null)
  }

  if (!selectedStep) {
    return (
      <div className="p-4 space-y-4">
        <div className="card bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <span className="text-white text-xl font-black">연</span>
            </div>
            <div>
              <h2 className="text-lg font-black text-slate-800">연립일차방정식</h2>
              <p className="text-xs text-slate-500">A2-3 · 중학교 2학년 방정식</p>
            </div>
          </div>
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-slate-500">
              <span>학습 진도</span>
              <span className="font-bold text-emerald-600">{completedCount}/{TOTAL_LEARN_STEPS} 완료</span>
            </div>
            <div className="h-2.5 bg-emerald-100 rounded-full overflow-hidden">
              <div className="h-2.5 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full transition-all duration-500"
                style={{ width: `${progressPct}%` }} />
            </div>
          </div>
        </div>

        <PredictedFragmentPanel fragments={predictedFragments} />

        <div className="space-y-3">
          {STEPS.map((step, idx) => {
            const sp = learnProgress[step.id]
            const isDone = sp?.status === 'completed' || sp?.status === 'mastered'
            const video = getApprovedByTag(step.id)
            return (
              <div key={step.id}
                className={`card border-2 cursor-pointer transition-all hover:shadow-md ${isDone ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 hover:border-emerald-200'}`}
                onClick={() => { setSelectedStep(step); setActiveTab('story') }}>
                <div className="flex items-center gap-3">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${isDone ? 'bg-emerald-100' : step.lightColor}`}>
                    {isDone ? <CheckCircle size={22} className="text-emerald-500" /> : <span className="text-xl">{step.icon}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${step.difficultyColor}`}>{step.difficulty}</span>
                      {video && (
                        <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
                          <Youtube size={10} /> 영상
                        </span>
                      )}
                    </div>
                    <p className={`text-sm font-bold ${isDone ? 'text-emerald-700' : 'text-slate-800'}`}>
                      {idx + 1}. {step.title}
                    </p>
                    <p className="text-xs text-slate-400 truncate">{step.subtitle}</p>
                  </div>
                  <ChevronRight size={16} className="text-slate-400 flex-shrink-0" />
                </div>
                <div className="flex gap-1.5 mt-2 flex-wrap">
                  {step.tags.map(t => (
                    <span key={t} className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{t}</span>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  const story = STEP_STORIES[selectedStep.id]
  const sp = learnProgress[selectedStep.id]
  const isDone = sp?.status === 'completed' || sp?.status === 'mastered'
  const video = getApprovedByTag(selectedStep.id)

  const tabs = [
    { key: 'story' as const, label: '스토리', icon: <MessageCircle size={13} /> },
    { key: 'concept' as const, label: '개념', icon: <BookOpen size={13} /> },
    { key: 'examples' as const, label: '예제', icon: <FileText size={13} /> },
    { key: 'practice' as const, label: '연습', icon: <PenTool size={13} /> },
  ]

  return (
    <div className="p-4 space-y-4">
      <button onClick={() => setSelectedStep(null)}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 transition-colors">
        ← 목록으로
      </button>

      <div className={`card border-2 ${selectedStep.borderColor} ${selectedStep.lightColor}`}>
        <div className="flex items-start gap-3">
          <div className={`w-12 h-12 rounded-xl ${selectedStep.color} flex items-center justify-center flex-shrink-0`}>
            <span className="text-white text-xl">{selectedStep.icon}</span>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${selectedStep.difficultyColor}`}>{selectedStep.difficulty}</span>
              <span className="text-xs text-slate-400">{selectedStep.id}</span>
              {isDone && <CheckCircle size={14} className="text-emerald-500" />}
            </div>
            <h3 className="text-base font-black text-slate-800">{selectedStep.title}</h3>
            <p className="text-xs text-slate-500 mt-0.5">{selectedStep.subtitle}</p>
          </div>
        </div>
        <div className="mt-3 p-3 bg-white/70 rounded-xl border border-white">
          <div className="flex items-center gap-1.5 mb-1">
            <Target size={12} className={selectedStep.textColor} />
            <p className="text-xs font-bold text-slate-600">학습 목표</p>
          </div>
          <p className="text-xs text-slate-700 leading-relaxed">{selectedStep.objective}</p>
        </div>
        {video && (
          <button onClick={() => setVideoModal({ stepId: selectedStep.id, label: selectedStep.title })}
            className="mt-2 w-full flex items-center justify-center gap-2 py-2 rounded-xl bg-red-50 border border-red-200 text-red-600 text-xs font-bold hover:bg-red-100 transition-colors">
            <Youtube size={13} /> 개념 영상 보기
          </button>
        )}
      </div>

      <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
        {tabs.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold transition-all ${
              activeTab === tab.key ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}>
            {tab.icon}{tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'story' && story && <StoryCard story={story} />}

      {activeTab === 'concept' && (
        <div className="card space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <Brain size={15} className={selectedStep.textColor} />
            <h4 className="text-sm font-bold text-slate-800">핵심 개념</h4>
          </div>
          <div className="bg-slate-50 rounded-xl p-4 text-xs text-slate-700 leading-relaxed whitespace-pre-wrap font-mono">
            {selectedStep.concept}
          </div>
          <div className="space-y-2">
            <p className="text-xs font-bold text-slate-600 flex items-center gap-1">
              <Star size={12} className="text-amber-400" /> 핵심 포인트
            </p>
            {selectedStep.keyPoints.map((kp, i) => (
              <div key={i} className="flex items-start gap-2 p-2 bg-amber-50 rounded-lg border border-amber-100">
                <CheckCircle size={13} className="text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-slate-700">{kp}</p>
              </div>
            ))}
          </div>
          {selectedStep.misconceptions.length > 0 && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-center gap-1.5 mb-1.5">
                <AlertTriangle size={12} className="text-red-500" />
                <p className="text-xs font-bold text-red-600">흔한 오개념 주의</p>
              </div>
              <div className="flex flex-wrap gap-1">
                {selectedStep.misconceptions.map(code => (
                  <span key={code} className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-mono">{code}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'examples' && (
        <div className="space-y-3">
          {selectedStep.examples.map((ex, i) => (
            <ExampleCard key={i} example={ex} idx={i} />
          ))}
        </div>
      )}

      {activeTab === 'practice' && (
        <PracticeBox practice={selectedStep.practice} stepId={selectedStep.id} />
      )}

      {!isDone ? (
        <button onClick={() => handleComplete(selectedStep)}
          className={`w-full py-3 ${selectedStep.color} hover:opacity-90 text-white font-bold rounded-2xl transition-all flex items-center justify-center gap-2 text-sm`}>
          <CheckCircle size={16} /> 이 단계 완료하기
        </button>
      ) : (
        <div className="w-full py-3 bg-emerald-100 text-emerald-700 font-bold rounded-2xl flex items-center justify-center gap-2 text-sm border border-emerald-200">
          <CheckCircle size={16} /> 완료한 단계입니다 ✓
        </div>
      )}

      {videoModal && (
        <ConceptVideoModal
          conceptTag={videoModal.stepId}
          conceptLabel={videoModal.label}
          onClose={() => setVideoModal(null)}
        />
      )}
    </div>
  )
}
